import * as React from 'react';
import * as AllComponents from '../../home/components';
import { PlusCircleOutlined, LeftOutlined, DownOutlined, FormOutlined, FileDoneOutlined, DeleteOutlined, CheckOutlined, HistoryOutlined } from '@ant-design/icons';
import { enmEntityState, enmOrderingType, ICallOptions, IEntity, IFetchingInfo, IFetchingResult, IOrdering, IPaging } from "../infrastructure/models";
import { enmControlType, enmEntityManagerState, enmPropertyType, IEntityManager, IEntityManagerProps, IEntityManagerStates, IInitialData, IInitialParams, IMetadata, IProperty } from "./interfaces"
import { Button, Checkbox, Col, FormInstance, Popconfirm, Row, Table, Tooltip } from 'antd'
import { Core } from '../infrastructure/core';
import { ColumnType } from 'antd/lib/table';
import { ResizableTitle } from './resizabletitle';
import './index.css';
import SearchForm from './searchform';
import DataForm from './dataform';
import { BaseComponent } from '../infrastructure/baseComponent';
import CustomSpinner from '../infrastructure/customSpinner';
import { AndFilter, ComparisonFilter, enmConditionalOperators, IFilter, OrFilter, TrueFilter } from '../infrastructure/filtering';
import dayjs from 'dayjs';
import { AuditLogItems } from '../../baseInfo/auditLogItems';

export class BaseEntityManager<TEntity extends IEntity, TProps extends IEntityManagerProps<TEntity>, TStates extends IEntityManagerStates<TEntity>, TFetchingInfo extends IFetchingInfo, TInitialData extends IInitialData>
    extends BaseComponent<TProps, TStates> implements IEntityManager {

    initialData?: TInitialData;
    paging?: IPaging;
    ordering?: IOrdering;
    filter?: IFilter;
    headerFilter?: IFilter;
    metadata: IMetadata;
    preLoad?: boolean;
    originalFilter?: () => IFilter | undefined;
    originalOrderings?: () => IOrdering[];
    onLoaded?: (initialData: TInitialData) => void;
    onCellClick?: (data: any, dataIndex?: string, index?: number, event?: React.MouseEvent<TEntity, MouseEvent>) => any;
    onRowClick?: (data: any, index?: number, event?: React.MouseEvent<TEntity, MouseEvent>) => any;
    onDataFetching?: (fetchingInfo: TFetchingInfo, onViewMode?: boolean) => void;
    onDataFetched?: (result: IFetchingResult<TEntity>) => IFetchingResult<TEntity>;
    renderingToolbar?: () => React.ReactNode;
    onEntityView?: (entity: TEntity) => boolean;
    onEntityAdded?: (entity: TEntity) => Promise<boolean> | boolean;
    onEntityEdit?: (entity: TEntity) => boolean;
    onEntityDelete?: (entity: TEntity) => boolean;
    onEntitySaving?: (entity: TEntity) => boolean;
    onEntitySaved?: (entity: TEntity) => void;
    onEntityChanged?: (entity: TEntity, propertyName: string, form: FormInstance<any>) => void;
    onFirstDataLoad?: () => void;
    getInitialParams?: () => IInitialParams;
    onSearch?: (value: any) => void;
    searchable?: boolean;
    searchArea?: {
        onSubmit?: (values: any, filter?: IFilter) => void,
        onReset?: () => void,
        render?: () => React.ReactNode[],
        renderExtraButtons?: () => React.ReactNode,
        columnsPerRow?: number;
        onFilterChanged?: (property: IProperty, value: any, form?: FormInstance<any>) => void;
        defaultValues?: () => {
            [key: string]: { operator: enmConditionalOperators; value?: any; };
        };
    };
    extraHeaderButtons?: () => React.ReactNode[];
    extraHeaderNodes?: () => React.ReactNode;
    backNode?: (state: enmEntityManagerState) => React.ReactNode;
    defaultSelectedRow?: () => TEntity | undefined;
    onStateChanged?: (prevSate: enmEntityManagerState, state: enmEntityManagerState) => void;
    extraViewNode?: () => React.ReactNode;
    setRecordClassName?: (record: TEntity, rowIndex?: number) => string | null;
    private _gridContainer?: HTMLElement | null;
    private _gridTable?: HTMLDivElement | null;
    protected preparelookupProps?: (propName: string, lookupProps: any, form?: FormInstance<any>, isSearchForm?: boolean) => any;
    protected onLookupOpen?: (propName: string) => void;
    protected onLookupClose?: (propName: string, selectetEntity?: any) => void;

    protected viewRef?: React.ReactInstance | null;

    selectedEntity?: TEntity;

    getDefaultState?: () => any;

    content?: any = null;

    viewButtonTitle = (): string => {
        return "نمایش";
    };

    insertButtonTitle = (): string => {
        return "ایجاد";
    };

    protected baseUrl: string = "";
    protected getUrl: string = "";
    protected getInitialDataUrl: string = "";
    protected defaultId?: number;
    protected extraFormNodeMarginTop?: number;

    constructor(props: TProps) {
        super(props)

        this.paging = this.paging ?? { PageNo: 1, PageSize: 10 };

        this.metadata = (this.constructor as any).initMetadata && (this.constructor as any).initMetadata();
        if (!this.metadata)
            throw new Error((this.constructor as any).name + ' metadata not found...');

        this.backNode = props.backNode;

        this.onInitializing();

        if (!this.baseUrl)
            this.baseUrl = this.metadata.name.toLowerCase();
        if (!this.getUrl)
            this.getUrl = this.baseUrl + "/get";
        if (!this.getInitialDataUrl)
            this.getInitialDataUrl = this.baseUrl + "/getinitialdata";

        const defaultState = {
            state: enmEntityManagerState.List,
            loading: true,
            data: [],
            columns: this.getColumns(this.metadata),
            expandedRowKeys: [],
            isAuthorized: true
        };
        this.preLoad = this.preLoad || this.props.preLoad;
        this.state = Object.assign(defaultState, this.getDefaultState! && this.getDefaultState());
        this.title = this.metadata.title;

        this.defaultId = this.props.defaultId;


    }

    getColumns(metadata: IMetadata): any {
        const columns = metadata.Properties.filter(property => property.showColumn !== false).map(property => {
            let newCol: ColumnType<any> = {};
            newCol.key = property.key ?? property.dataIndex;
            newCol.title = property.title;
            newCol.dataIndex = property.dataIndex;
            newCol.width = property.width;
            newCol.onCell = (record, rowIndex) => {
                return {
                    onClick: (event) => {
                        if (this.onCellClick)
                            this.onCellClick.call(this, record, property.dataIndex, rowIndex, event);
                    },
                };
            };
            newCol.fixed = property.fixed;
            newCol.responsive = property.responsive ?? ['lg', 'md', 'sm', 'xs'];
            newCol.render = (text, record: TEntity, index) => this.renderGridColumn(text, record, index, property);
            newCol.sorter = property.sortable !== false;
            return newCol;
        });

        columns.push(Table.EXPAND_COLUMN);
        return columns;
    }

    onInitializing() {

    }

    private renderGridColumn = (text: any, record: TEntity, index: number, property: IProperty) => {
        let result: any = null;
        const width = this.state.columns?.find((col: ColumnType<TEntity>) => col.dataIndex?.toString() === property.dataIndex)?.width ?? property.width ?? 150;
        if (property.renderColumn) {
            result = property.renderColumn(text, record, index);
        }

        if (property.lookup?.lookupComponent && record) {
            let displayData = record as any;
            if (displayData[property.lookup?.lookupProperty!])
                return (
                    <div key={record.RowGuid}>
                        <Col style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width }}>
                            {property.lookup?.lookupDisplayFields?.map(f => {
                                let fields = f.split('.');
                                let value = displayData[property.lookup?.lookupProperty!];
                                fields.forEach(i => {
                                    if (value !== undefined && value != null && value[i] !== undefined && value[i] != null)
                                        value = value[i];
                                    else
                                        value = undefined;
                                });
                                return value + " "
                            })}</Col>
                    </div>
                );
        }

        result = result ?? text;

        let fields = property.dataIndex?.split('.');
        let value = record as any;
        fields?.forEach(i => {
            if (value !== undefined && value != null && value[i] !== undefined && value[i] != null)
                value = value[i];
            else
                value = undefined;
        });

        if (property.propertyType === enmPropertyType.boolean) {
            return (<Checkbox key={record.RowGuid} checked={value} />);
        }
        if (property.combobox && (property.combobox.options || property.combobox.optionsFunc)) {
            return (
                <div key={record.RowGuid} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width }}>
                    {property.combobox.options?.find(o => o.value === value)?.title}
                    {property.combobox.optionsFunc && property.combobox.optionsFunc(this, record).find(o => o.value === value)?.title}
                </div>
            );
        }
        if (property.propertyType === enmPropertyType.date) {
            result = text ? dayjs(text).calendar('jalali').locale('fa').format("YYYY/MM/DD") : undefined;
            return (<div key={record.RowGuid} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width }}>
                {result}
            </div>);
        }
        if (property.propertyType === enmPropertyType.datetime) {
            result = value ? dayjs(value).calendar('jalali').locale('fa').format("YYYY/MM/DD HH:mm") : undefined;
            return (<div key={record.RowGuid} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width }}>
                {result}
            </div>);
        }
        if (property.controlType === enmControlType.numberinput) {
            result = text;
            if (value)
                result = value;
            return (<div key={record.RowGuid} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width }}>
                {property.formatter ? property.formatter(result, {} as any) : result}
            </div>);
        }
        if (property.controlType === enmControlType.priceinput) {
            result = text;
            if (value)
                result = value;
            return (<div key={record.RowGuid} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width }}>
                {`${result}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            </div>);
        }
        if (!result && !!value)
            result = value;
        return (
            <div key={record.RowGuid} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width }}>
                {result}
            </div>
        );
    };

    private handleColumnResize = (index: number) => (_: any, { size }: any) => {
        this.setState(({ columns }) => {
            const nextColumns = [...columns];
            const resizedCol = nextColumns[index] as any;
            nextColumns[index] = {
                ...resizedCol,
                width: size.width,
            };
            return { columns: nextColumns };
        });
    };

    componentDidMount() {
        super.componentDidMount();
        if (!this.props.isLookupMode) {
            const { params } = this.context;
            const defaultId = params['defaultid'];
            if (!this.defaultId && defaultId)
                this.defaultId = defaultId;
        }
        if (this.defaultId)
            this.originalFilter = () => ComparisonFilter.Equal<TEntity>(i => i.Id, this.defaultId);

        this.getInitialData();
    }

    private initSearchForm = () => {
        return (<SearchForm
            parent={this}
            properties={this.metadata.Properties}
            loading={this.state.loading}
            columnsPerRow={this.searchArea?.columnsPerRow}
            onSubmit={(values, filter) => {
                if (this.searchArea?.onSubmit)
                    this.searchArea.onSubmit(values, filter);
                this.filter = filter ?? new TrueFilter();
                this.fetchData();
            }}
            onReset={() => {
                this.filter = undefined;
                if (this.searchArea?.onReset)
                    this.searchArea.onReset();
            }}
            onSearch={value => {
                if (this.onSearch)
                    this.onSearch(value);
                this.paging = { PageNo: 1, PageSize: this.paging?.PageSize ?? 10 };
                this.search(value);
            }}
            defaultValues={this.searchArea?.defaultValues}
            onFilterChanged={this.searchArea?.onFilterChanged}
            render={this.searchArea?.render}
            renderExtraButtons={this.searchArea?.renderExtraButtons}
            style={{ marginTop: this.props.showHeader === false ? 0 : 10, background: '#fff', boxShadow: 'rgb(0 0 0 / 12%) 0px 0px 5px 2px' }}
            preparelookupProps={(propName, lookupProps, form) => {
                if (this.preparelookupProps)
                    return this.preparelookupProps(propName, lookupProps, form, true);
                return lookupProps;
            }}
            onLookupOpen={this.onLookupOpen}
            onLookupClose={this.onLookupClose}
        />);
    }

    componentDidUpdate(prevProp: Readonly<TProps>, prevState: Readonly<TStates>, snapshot?: any) {
        super.componentDidUpdate && super.componentDidUpdate(prevProp, prevState, snapshot);

        if (prevState.state !== this.state.state) {
            if (this.onStateChanged)
                this.onStateChanged(prevState.state, this.state.state);
            if (this.props.onStateChanged)
                this.props.onStateChanged(prevState.state, this.state.state);
        }
        if (prevState.totalResultCount === undefined && this.state.totalResultCount !== undefined) {
            if (this.onFirstDataLoad)
                this.onFirstDataLoad();
        }
    }

    componentWillUnmount() {
        super.componentWillUnmount();
    }

    async getInitialData() {
        let initialParams: IInitialParams = this.getInitialParams ? this.getInitialParams() : {};
        let fetchOpt: ICallOptions = {
            url: this.getInitialDataUrl,
            data: { initialParams },
            onSuccess: (result) => {
                if (!result.ValidationState.Successful)
                    return;
                let res: any = result;
                this.onLoaded?.call(this, res.Data);
                this.props.onLoaded?.call(this, res.Data);
                this.initialData = res.Data;
                if (this.preLoad !== false)
                    this.fetchData();
            },
            onError: () => {

            }
        };
        await this.callApi(fetchOpt);
    }

    getCurrentFetchingInfo = () => {
        let fetchingInfo = {
            Orderings: [] as IOrdering[]
        } as TFetchingInfo;

        fetchingInfo.Orderings = this.originalOrderings?.call(this) ?? [];
        if (fetchingInfo.Filter) {
            if (this.originalFilter) {
                let originalFilter = this.originalFilter.call(this);
                if (originalFilter)
                    fetchingInfo.Filter = fetchingInfo.Filter.And(originalFilter);
            }
        }
        else {
            fetchingInfo.Filter = this.originalFilter?.call(this);
        }

        if (this.filter) {
            if (fetchingInfo.Filter)
                fetchingInfo.Filter = fetchingInfo.Filter.And(this.filter);
            else
                fetchingInfo.Filter = this.filter;
        }
        else if (this.searchArea?.defaultValues && !this.props.isLookupMode) {
            const defaultSearchValues = this.searchArea.defaultValues();
            let filters: IFilter[] = [];
            for (let key in defaultSearchValues) {
                let filter = defaultSearchValues[key];
                if (filter && filter.value !== undefined)
                    filters.push(new ComparisonFilter(key, filter.operator, filter.value))
            }

            if (fetchingInfo.Filter)
                fetchingInfo.Filter = fetchingInfo.Filter.And(filters);
            else
                fetchingInfo.Filter = new AndFilter(filters);
        }

        if (this.headerFilter) {
            if (fetchingInfo.Filter) {
                fetchingInfo.Filter = fetchingInfo.Filter.And(this.headerFilter);
            }
            else {
                fetchingInfo.Filter = this.headerFilter;
            }
        }

        fetchingInfo.Paging = this.paging;

        if (this.ordering)
            fetchingInfo.Orderings.splice(0, 0, this.ordering);

        this.onDataFetching?.call(this, fetchingInfo);
        return fetchingInfo;
    }

    async fetchData(handleResultByInvoker?: boolean) {
        let fetchingInfo = this.getCurrentFetchingInfo();

        let fetchOpt: ICallOptions = {
            url: this.getUrl,
            data: { fetchingInfo },
            onSuccess: (result) => {
                if (!result.ValidationState.Successful)
                    return;

                let res: any = result;
                if (this.onDataFetched)
                    res = this.onDataFetched(res);

                if (handleResultByInvoker && this.props.onDataFetched) {
                    this.props.onDataFetched(result as IFetchingResult<TEntity>);
                    return;
                }

                let defaultRow = this.defaultSelectedRow ? this.defaultSelectedRow() : this.state.currentEntity ?? this.selectedEntity;
                if (defaultRow?.RowGuid) {
                    if (!this.selectedEntity)
                        this.selectedEntity = defaultRow;
                }

                if (this.selectedEntity?.RowGuid) 
                    this.selectedEntity = (res.Data as TEntity[]).find(i => i.RowGuid === this.selectedEntity?.RowGuid);

                let expandedRowKeys: React.Key[] = [];
                if (this.selectedEntity?.RowGuid && defaultRow?.RowGuid)
                    expandedRowKeys.push(defaultRow.RowGuid);

                const defaultEntity = this.props.defaultId ? (res.Data as TEntity[]).find(i => i.Id === this.props.defaultId) : undefined;
                if (defaultEntity && this.props.defaultState === enmEntityManagerState.View) {
                    if (this.onEntityView) {
                        if (!this.onEntityView(defaultEntity))
                            return;
                    }
                }
                this.setState({ data: res.Data, totalResultCount: res.TotalResultCount, state: this.props.defaultState ?? enmEntityManagerState.List, currentEntity: defaultEntity, expandedRowKeys });
            },
            onError: () => {
                if (this.props.onDataFetched)
                    this.props.onDataFetched({ Data: [], TotalResultCount: 0, ValidationState: { Successful: false, ValidationResults: [] } });
            }
        };
        await this.callApi(fetchOpt);
    }

    onLookupClosed = () => {
        this.closeLookupForm();
        this.fetchData();
    }

    onTableRowExpand(expanded: boolean, record: TEntity) {
        var keys: React.Key[] = [];
        if (expanded) {
            keys.push(record.RowGuid);
        }
        this.setState({ expandedRowKeys: keys });
    }

    async view(rowGuid: string) {
        let fetchingInfo = {
            Orderings: [] as IOrdering[],
            Paging: { PageNo: 1, PageSize: 1 } as IPaging,
            Filter: ComparisonFilter.Equal<TEntity>(e => e.RowGuid, rowGuid) as IFilter
        } as TFetchingInfo;

        this.onDataFetching?.call(this, fetchingInfo, true);

        let fetchOpt: ICallOptions = {
            url: this.getUrl,
            data: { fetchingInfo },
            onSuccess: (result) => {
                if (!result.ValidationState.Successful)
                    return;
                let res: any = result;
                if (this.onDataFetched)
                    res = this.onDataFetched(res);
                if (this.onEntityView) {
                    if (!this.onEntityView(res.Data[0]))
                        return;
                }
                this.setState({ state: enmEntityManagerState.View, currentEntity: res.Data[0] });
            },
            onError: () => {

            }
        };
        await this.callApi(fetchOpt);
    }

    async edit(rowGuid: string) {
        let fetchingInfo = {
            Orderings: [] as IOrdering[],
            Paging: { PageNo: 1, PageSize: 1 } as IPaging,
            Filter: ComparisonFilter.Equal<TEntity>(e => e.RowGuid, rowGuid) as IFilter
        } as TFetchingInfo;

        this.onDataFetching?.call(this, fetchingInfo);

        let fetchOpt: ICallOptions = {
            url: this.getUrl,
            data: { fetchingInfo },
            onSuccess: (result) => {
                if (!result.ValidationState.Successful)
                    return;
                let res: any = result;
                if (this.onEntityEdit) {
                    if (!this.onEntityEdit(res.Data[0]))
                        return;
                }
                this.setState({ state: enmEntityManagerState.Edit, currentEntity: res.Data[0] });
            },
            onError: () => {

            }
        };
        await this.callApi(fetchOpt);
    }

    async showHistory(rowGuid: string) {
        this.addLookupForm("EntityHistory",
            <AuditLogItems
                key="EntityHistory"
                metadata={this.metadata}
                entityRowGuid={rowGuid}
                invokePath={this.metadata.title}
                onClose={this.onLookupClosed}
            />
        );
    }

    async add() {
        let entity: any = { RowGuid: Core.newId(), State: enmEntityState.Added, ModifiedProperties: [], Id: 0, CreatorId: 0 };
        if (this.onEntityAdded) {
            if (! await this.onEntityAdded(entity))
                return;
        }
        this.setState({ state: enmEntityManagerState.New, currentEntity: entity });
    }

    async remove(rowGuid: string) {
        let fetchOpt: ICallOptions = {
            url: this.baseUrl + "/getbyguid",
            data: { rowGuid },
            onSuccess: async (result) => {
                if (!result.ValidationState.Successful)
                    return;
                let res: any = result;
                if (this.onEntityDelete) {
                    if (!this.onEntityDelete(res.Data))
                        return;
                }
                if (res.Data) {
                    res.Data.State = enmEntityState.Deleted;
                    await this.save(res.Data);
                }
            },
            onError: () => {

            }
        };
        await this.callApi(fetchOpt);
    }

    async save(entity: TEntity): Promise<boolean> {
        let data: any = {};
        if (!entity)
            return false;

        if (this.onEntitySaving) {
            if (!this.onEntitySaving(entity))
                return false;
        }

        if (this.props.onEntitySaving) {
            if (!this.props.onEntitySaving(entity))
                return false;
        }
        if (entity.State === enmEntityState.Modified || entity.State === enmEntityState.Added) {
            for (var i = 0; i < entity.ModifiedProperties.length; i++) {
                let val = entity.ModifiedProperties[i];
                data[val] = (entity as any)[val];
            }
            data["ModifiedProperties"] = entity.ModifiedProperties
        }
        else if (entity.State === enmEntityState.Unchanged) {
            Core.warningNotification('لطفا اطلاعات را اصلاح سپس ذخیره نمایید');
            return false;
        }
        data["RowGuid"] = entity.RowGuid;
        data["RowVrsn"] = entity.RowVrsn;
        data["State"] = entity.State;
        let url = "";
        switch (entity.State) {
            case enmEntityState.Modified:
                url = "/edit";
                break;
            case enmEntityState.Deleted:
                url = "/delete";
                break;
            default:
                url = "/add";
                break;
        }
        let fetchOpt: ICallOptions = {
            url: this.baseUrl + url,
            data: { entities: [data] },
            onSuccess: (result) => {
                if (!result.ValidationState.Successful)
                    return;
                let res: any = result;
                if (this.onEntitySaved) {
                    this.onEntitySaved(res.Data[0]);
                }
                if (this.props.onEntitySaved) {
                    this.props.onEntitySaved(res.Data[0]);
                }

                if (result.ValidationState.Successful) {
                    Core.successNotification('تغییرات با موفقیت ذخیره شد');
                    this.fetchData();
                }
            },
            onError: () => {

            }
        };
        return await this.callApi(fetchOpt);
    }

    onBack?: () => void;

    back() {
        this.onBack && this.onBack();
        this.state.state === enmEntityManagerState.List ? window.history.back() : this.fetchData();
    }

    getHeaderFilter = (property: IProperty, value: any, navigationName?: string): IFilter | undefined => {
        if (property.filterable === false)
            return;
        if (!navigationName && property.controlType === enmControlType.lookup && !!property.lookup && !!property.lookup.lookupComponent) {
            const lookup = typeof property.lookup.lookupComponent === "string" ? (AllComponents as any)[property.lookup.lookupComponent as string] : property.lookup.lookupComponent;;
            const lookupMetadata: IMetadata = lookup.initMetadata();
            if (!!lookupMetadata) {
                const displayLookupProperties = lookupMetadata.Properties.filter(i => i.filterable !== false) ?? [];
                let filters: IFilter[] = [];
                displayLookupProperties.forEach(i => {
                    const filter = this.getHeaderFilter(i, value, (!!navigationName ? navigationName + '.' : '') + property.lookup!.lookupProperty);
                    if (!!filter)
                        filters.push(filter);
                });
                if (filters.length)
                    return new OrFilter(filters);
            }
        }
        else if (property.controlType === enmControlType.combobox && !!property.combobox) {
            const comboboxOptions = property.combobox.options ?? (property.combobox.optionsFunc && property.combobox.optionsFunc(this, value));
            if (!!comboboxOptions) {
                if (isNaN(value) && property.propertyType === enmPropertyType.number) {
                    const currentOpt = comboboxOptions.find(o => o.title === value);
                    if (!!currentOpt)
                        return new ComparisonFilter((!!navigationName ? navigationName + '.' : '') + property.dataIndex, enmConditionalOperators.Like, currentOpt.value);
                }
            }
        }
        else if (property.dataIndex) {
            if ((!isNaN(value) && property.propertyType === enmPropertyType.number) || property.propertyType === enmPropertyType.string)
                return new ComparisonFilter((!!navigationName ? navigationName + '.' : '') + property.dataIndex, enmConditionalOperators.Like, value);
        }
    }

    async search(value?: any, selectedRowGuid?: string, paging?: IPaging, ordering?: IOrdering, handleResultByInvoker?: boolean) {
        let filters: IFilter[] = [];
        if (selectedRowGuid) {
            filters.push(new ComparisonFilter("RowGuid", enmConditionalOperators.Equal, selectedRowGuid));
        }
        else if (value) {
            this.metadata.Properties.filter(p => !p.lookup || (!!p.lookup && this.metadata.excludeNavigationFromSearch !== true))
                .forEach(p => {
                    const filter = this.getHeaderFilter(p, value);
                    if (!!filter)
                        filters.push(filter);
                });
        }

        if (filters.length)
            this.headerFilter = new OrFilter(filters);
        else
            this.headerFilter = undefined;

        if (ordering)
            this.ordering = ordering;

        if (paging)
            this.paging = paging;

        await this.fetchData(handleResultByInvoker);
    }

    enableAddCommand?: () => boolean;
    visibleAddCommand?: () => boolean;

    enableEditCommand?: (entity: TEntity) => boolean;
    visibleEditCommand?: (entity: TEntity) => boolean;

    enableDeleteCommand?: (entity: TEntity) => boolean;
    visibleDeleteCommand?: (entity: TEntity) => boolean;

    protected rightSideExtraComponent?: {
        render: () => React.ReactNode;
        colSpan: number;
    };

    virtualRender() {

        const columns = this.state.columns.map((col, index) => ({
            ...col,
            onHeaderCell: (column: any) => ({
                width: column.width,
                onResize: this.handleColumnResize(index)
            }),
        }));

        const { PageNo, PageSize } = this.paging!;

        const components = {
            header: {
                cell: ResizableTitle,
            },
        };

        const dataSource = this.state?.data;
        const extra: React.ReactNode[] = [];

        if (this.state.state === enmEntityManagerState.List && this.initialData?.AccessList["Add"] && this.visibleAddCommand?.call(this) !== false) {
            const insertTitle = this.insertButtonTitle();
            extra.push(<Tooltip key="1" title={insertTitle + " " + this.metadata.title}><Button disabled={this.enableAddCommand?.call(this) === false} style={{ lineHeight: '0.5em', verticalAlign: 'middle' }} type="primary" onClick={() => this.add()} icon={<PlusCircleOutlined />}>{insertTitle}</Button></Tooltip>);
        }

        if (this.extraHeaderButtons)
            extra.push(...this.extraHeaderButtons());

        let title = this.metadata.title;
        let modeTitle = "";
        if (this.state.state === enmEntityManagerState.New) {
            modeTitle = " (جدید) ";
        }
        if (this.state.state === enmEntityManagerState.Edit) {
            modeTitle = " (ویرایش) ";
        }
        const loading = this.state.loading || !dataSource ? { indicator: <CustomSpinner /> } : false;
        const extraHeaderNodes = this.extraHeaderNodes && this.extraHeaderNodes();
        const header = this.props.showHeader === false
            ? undefined
            : this.getPageHeader({
                title,
                subTitle: modeTitle,
                onBack: this.props.hasBack !== false ? () => {
                    if (this.state.state === enmEntityManagerState.List && this.props.onClose)
                        this.props.onClose(this);
                    else
                        this.back();
                } : undefined,
                extraHeaderButtons: extra,
                extraHeaderNodes: extraHeaderNodes ?? undefined,
                backNode: this.backNode && this.backNode(this.state.state),
                affix: this.state.state === enmEntityManagerState.View
            });

        let extraFormNode: React.ReactNode = undefined;
        switch (this.state.state) {
            case enmEntityManagerState.New:
                break;
            case enmEntityManagerState.Edit:
                break;
            case enmEntityManagerState.View:
                extraFormNode = (this.props.extraViewNode && this.props.extraViewNode()) ?? (this.extraViewNode && this.extraViewNode());
                break;
            default:
                break;
        }

        return (
            <div>
                {header}

                <div style={{ display: this.state.state === enmEntityManagerState.List && !this.state.currentEntity ? 'block' : 'none' }}>
                    {this.searchable && this.initSearchForm()}

                    <div className="row" style={this.rightSideExtraComponent?.colSpan ? { margin: 0, marginTop: 10 } : { marginTop: 10 }}>
                        {this.rightSideExtraComponent &&
                            <div className={`col-md-${this.rightSideExtraComponent.colSpan}`}
                                style={{
                                    margin: 0,
                                    padding: 0,
                                    paddingBottom: this.state.windowSize === 1 ? 10 : 0
                                }}>
                                {this.rightSideExtraComponent.render()}
                            </div>
                        }
                        <div className={this.rightSideExtraComponent?.colSpan ? `col-md-${12 - this.rightSideExtraComponent.colSpan}` : ""}
                            style={this.rightSideExtraComponent?.colSpan
                                ? {
                                    margin: 0,
                                    padding: 0,
                                    paddingRight: this.state.windowSize === 1 ? 0 : 10
                                }
                                : {}
                            }>
                            <div ref={node => this._gridContainer = node}>
                                <Table
                                    className="box"
                                    rowKey="RowGuid"
                                    components={components}
                                    columns={columns}
                                    rowClassName={(_, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
                                    dataSource={dataSource}
                                    scroll={{ x: 'max-content' }}
                                    loading={loading}
                                    sticky={{ getContainer: () => { return this._gridContainer!; } }}
                                    showHeader={true}
                                    onChange={(pagination, _, sorter) => {
                                        if (!(sorter instanceof Array) && !!sorter.field && !!sorter.order) {
                                            this.ordering = {
                                                Property: sorter.field.toString(),
                                                OrderBy: sorter.order === 'ascend' ? enmOrderingType.Ascending : enmOrderingType.Descending
                                            };
                                        }
                                        else
                                            this.ordering = undefined;

                                        if (this.paging) {
                                            this.paging.PageNo = pagination.current ?? 1;
                                            this.paging.PageSize = pagination.pageSize ?? 10;
                                        }
                                        this.fetchData();
                                    }}
                                    onRow={(record: TEntity, rowIndex) => {
                                        let rowClassName = this.state.expandedRowKeys.find(r => r === record.RowGuid) ? "table-expanded-parent" : "";
                                        if (this.setRecordClassName) {
                                            const extraClassName = this.setRecordClassName(record, rowIndex);
                                            rowClassName += rowClassName + ' ' + extraClassName;
                                        }
                                        return {
                                            onClick: (event) => {
                                                this.selectedEntity = record;
                                                if (this.onRowClick)
                                                    this.onRowClick.call(this, record, rowIndex, event);
                                            },
                                            className: rowClassName
                                        };
                                    }}
                                    pagination={{
                                        style: { marginRight: 10 },
                                        position: ['bottomRight'],
                                        responsive: true,
                                        defaultPageSize: this.paging?.PageSize ?? 10,
                                        current: Math.ceil(this.state.totalResultCount ?? 0 / PageSize) < PageNo ? 1 : PageNo,
                                        total: this.state.totalResultCount,
                                        showSizeChanger: true,
                                        size: 'small',
                                        showTotal: (total, _) => {
                                            return (<div>تعداد کل ردیف ها: {total} </div>);
                                        }
                                    }}
                                    expandedRowKeys={this.state.expandedRowKeys}
                                    onExpand={(expanded, record) => {
                                        this.selectedEntity = record;
                                        this.onTableRowExpand(expanded, record);
                                        this._gridTable?.getElementsByClassName('ant-table-body')[0]?.scrollTo({ left: 1000 });
                                    }}
                                    expandable={{
                                        expandRowByClick: true,
                                        columnWidth: 10,
                                        showExpandColumn: true,
                                        indentSize: 1,
                                        expandedRowRender: () => {
                                            if (!this.selectedEntity)
                                                return <></>;

                                            let enableEdit = this.selectedEntity && this.enableEditCommand?.call(this, this.selectedEntity) !== false;
                                            let enableDelete = this.selectedEntity && this.enableDeleteCommand?.call(this, this.selectedEntity) !== false;
                                            let visibleEdit = this.selectedEntity && this.visibleEditCommand?.call(this, this.selectedEntity) !== false;
                                            let visibleDelete = this.selectedEntity && this.visibleDeleteCommand?.call(this, this.selectedEntity) !== false;

                                            return (
                                                <Row aria-colspan={12} style={{}}>
                                                    {this.props.isLookupMode ? <Col style={{ marginLeft: 10 }}><Button type="primary" className="bg-success" onClick={() => this.props.onClose && this.props.onClose(this)} icon={<CheckOutlined />}>انتخاب</Button></Col> : ""}
                                                    <Col style={{ marginLeft: 10 }}>
                                                        <Button type="default" onClick={() => this.view(this.state.expandedRowKeys[0].toString())} icon={<FileDoneOutlined />}>{this.viewButtonTitle()}</Button>
                                                    </Col>
                                                    {this.metadata.showHistory === true && this.initialData?.AccessList["AuditLogItems"] ?
                                                        <Col style={{ marginLeft: 10 }}>
                                                            <Button type="default" onClick={() => this.showHistory(this.state.expandedRowKeys[0].toString())} icon={< HistoryOutlined />}>تاریخچه</Button>
                                                        </Col>
                                                        : ""
                                                    }
                                                    {this.initialData?.AccessList["Edit"] && visibleEdit ?
                                                        <Col style={{ marginLeft: 10 }}>
                                                            <Button type="primary" disabled={!enableEdit} onClick={() => this.edit(this.state.expandedRowKeys[0].toString())} icon={< FormOutlined />}>ویرایش</Button>
                                                        </Col>
                                                        : ""
                                                    }
                                                    {this.initialData?.AccessList["Delete"] && visibleDelete ?
                                                        <Col style={{ marginLeft: 10 }}>
                                                            <Popconfirm disabled={!enableDelete} okButtonProps={{ size: "middle" }} cancelButtonProps={{ size: "middle" }} title="آیا مایلید ردیف جاری حذف شود؟" okText="بلی" cancelText="خیر" onConfirm={() => this.remove(this.state.expandedRowKeys[0].toString())} >
                                                                <Button type="primary" danger disabled={!enableDelete} icon={<DeleteOutlined />}>حذف</Button>
                                                            </Popconfirm>
                                                        </Col>
                                                        : ""
                                                    }
                                                    {this.renderingToolbar ? this.renderingToolbar() : <></>}
                                                </Row>
                                            );
                                        },
                                        expandIcon: ({ expanded, onExpand, record }) =>
                                            expanded ? (
                                                <Tooltip title="برای عدم مشاهد عملیات کلیک نمایید">
                                                    <DownOutlined onClick={e => onExpand(record, e)} />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="برای مشاهده عملیات روی سطر مورد نظر کلیک نمایید">
                                                    <LeftOutlined onClick={e => onExpand(record, e)} />
                                                </Tooltip>
                                            )
                                    }}
                                    locale={{ emptyText: this.state.loading ? "در حال بازیابی اطلاعات" : (this.state.totalResultCount === undefined ? " لطفا ابتدا اطلاعات را فیلتر نمایید" : "اطلاعاتی یافت نشد") }}
                                    ref={node => this._gridTable = node}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {[enmEntityManagerState.New, enmEntityManagerState.Edit, enmEntityManagerState.View].indexOf(this.state.state) > -1 && !!this.state.currentEntity &&
                    <div ref={node => this.viewRef = node} className="print-container">
                        <DataForm<TEntity>
                            parent={this}
                            style={this.props.dataformBoxShadow !== false ? {} : { boxShadow: "inherit" }}
                            currentEntity={this.state.currentEntity!}
                            properties={this.metadata.Properties}
                            isReadonly={this.state.state === enmEntityManagerState.View}
                            onSubmit={() => this.save(this.state.currentEntity!)}
                            onReset={() => this.back()}
                            onChange={(property, value, form) => {
                                let currentEntity = this.state.currentEntity as any;
                                if (currentEntity) {
                                    currentEntity[property.dataIndex!] = property.lookup?.lookupProperty && value ? value["Id"] : value;
                                    if (property.lookup?.lookupProperty)
                                        currentEntity[property.lookup.lookupProperty] = value;
                                    if (this.onEntityChanged)
                                        this.onEntityChanged(this.state.currentEntity!, property.dataIndex!, form);
                                    if (!this.state.currentEntity?.ModifiedProperties.find(i => i === property.dataIndex!))
                                        currentEntity.ModifiedProperties.push(property.dataIndex!);
                                    if (currentEntity.State !== enmEntityState.Added)
                                        currentEntity.State = enmEntityState.Modified;
                                }
                                this.setState({ currentEntity });
                            }}
                            preparelookupProps={(propName, lookupProps, form) => {
                                if (this.preparelookupProps)
                                    return this.preparelookupProps(propName, lookupProps, form, false);
                                return lookupProps;
                            }}
                            onLookupOpen={this.onLookupOpen}
                            onLookupClose={this.onLookupClose}
                        />
                        {extraFormNode ? <div style={{ marginTop: this.extraFormNodeMarginTop !== undefined ? this.extraFormNodeMarginTop : 10 }}>{extraFormNode}</div> : <></>}
                    </div>
                }
            </div>
        );
    }
}

export class EntityManager<TEntity extends IEntity, TProps extends IEntityManagerProps<TEntity>, TStates extends IEntityManagerStates<TEntity>> extends BaseEntityManager<TEntity, TProps, TStates, IFetchingInfo, IInitialData> {

}
